export type UseCase = 'general-ai' | 'knowledge' | 'meetings' | 'marketing' | 'automation' | 'workspace';
export type Stack = 'microsoft' | 'google' | 'mixed';
export type CompanySize = '2-10' | '11-50' | '51-200' | '201-1000';
export type Timeline = 'now' | 'quarter' | 'exploring';
export type Priority = 'security' | 'speed' | 'brand' | 'automation' | 'low-cost' | 'governance';

export interface ToolProfile {
  id: string;
  name: string;
  category: string;
  short: string;
  bestFor: string;
  caveat: string;
  useCases: UseCase[];
  stacks: Stack[];
  minSeats?: number;
  maxSeats?: number;
  pricing: {
    display: string;
    annualSeatUsd?: number;
    monthlySeatUsd?: number;
    promoSeatUsd?: number;
    custom?: boolean;
    notes: string;
  };
  scores: {
    setup: number;
    security: number;
    automation: number;
    brand: number;
    governance: number;
    lowCost: number;
  };
  monetization: {
    path: 'affiliate' | 'qualified-lead' | 'vendor-direct';
    status: 'ready-to-apply' | 'affiliate-ready' | 'lead-capture' | 'no-confirmed-affiliate';
    cta: string;
    url: string;
    estimatedValueEur: string;
    disclosure: string;
  };
  sourceUrls: string[];
  lastChecked: string;
}

export interface BuyerInput {
  useCase: UseCase;
  companySize: CompanySize;
  stack: Stack;
  budgetMonthlyUsd: number;
  priorities: Priority[];
  timeline: Timeline;
  email?: string;
  company?: string;
}

export const useCaseLabels: Record<UseCase, string> = {
  'general-ai': 'Asistente IA general para equipos',
  knowledge: 'Búsqueda y conocimiento interno',
  meetings: 'Reuniones, transcripción y CRM',
  marketing: 'Marketing, marca y contenido',
  automation: 'Automatización de procesos',
  workspace: 'IA integrada en Google/Microsoft Workspace',
};

export const stackLabels: Record<Stack, string> = {
  microsoft: 'Microsoft 365 / Teams',
  google: 'Google Workspace',
  mixed: 'Stack mixto o por decidir',
};

export const companySizeSeatEstimate: Record<CompanySize, number> = {
  '2-10': 5,
  '11-50': 20,
  '51-200': 75,
  '201-1000': 250,
};

export const tools: ToolProfile[] = [
  {
    id: 'chatgpt-business',
    name: 'ChatGPT Business',
    category: 'Asistente general seguro',
    short: 'Mejor primera compra horizontal para equipos que quieren ChatGPT, conectores y controles de workspace sin proyecto de integración pesado.',
    bestFor: 'Equipos de 2 a 200 personas que necesitan productividad general, análisis, escritura, ideación, Codex y agentes de workspace.',
    caveat: 'Para integraciones profundas, data residency avanzada o procurement enterprise, la ruta natural es Enterprise o lead cualificado.',
    useCases: ['general-ai', 'knowledge', 'automation'],
    stacks: ['microsoft', 'google', 'mixed'],
    minSeats: 2,
    maxSeats: 200,
    pricing: {
      display: 'Standard: $20/usuario/mes anual o $25 mensual. Premium: $100 anual o $125 mensual.',
      annualSeatUsd: 20,
      monthlySeatUsd: 25,
      notes: 'Premium multiplica uso; Enterprise es precio personalizado.',
    },
    scores: { setup: 88, security: 82, automation: 78, brand: 62, governance: 78, lowCost: 74 },
    monetization: {
      path: 'qualified-lead',
      status: 'no-confirmed-affiliate',
      cta: 'Solicitar shortlist + cálculo de ROI',
      url: 'https://openai.com/business/pricing/',
      estimatedValueEur: 'Lead B2B propio: 25-250 € según tamaño y urgencia.',
      disclosure: 'Sin afiliación confirmada; el valor está en capturar demanda y derivar consultoría/implantación.',
    },
    sourceUrls: ['https://openai.com/business/pricing/'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'claude-team',
    name: 'Claude Team',
    category: 'Asistente para análisis y escritura larga',
    short: 'Muy fuerte para redacción, análisis, conocimiento y flujos donde la calidad de respuesta pesa más que la integración nativa en Office.',
    bestFor: 'Consultoría, legal, investigación, estrategia, operaciones y equipos que valoran contexto largo y calidad de razonamiento.',
    caveat: 'Tiene límites por miembro; para más de 150 asientos o necesidades enterprise hay que escalar a Enterprise.',
    useCases: ['general-ai', 'knowledge', 'marketing'],
    stacks: ['mixed', 'google', 'microsoft'],
    minSeats: 2,
    maxSeats: 150,
    pricing: {
      display: 'Standard: $20/miembro/mes anual o $25 mensual. Premium: $100 anual o $125 mensual.',
      annualSeatUsd: 20,
      monthlySeatUsd: 25,
      notes: 'Precios US sin impuestos; varían por región.',
    },
    scores: { setup: 84, security: 78, automation: 66, brand: 68, governance: 70, lowCost: 76 },
    monetization: {
      path: 'qualified-lead',
      status: 'no-confirmed-affiliate',
      cta: 'Comparar contra ChatGPT Business',
      url: 'https://support.claude.com/en/articles/9266767-what-is-the-team-plan',
      estimatedValueEur: 'Lead comparativo: 20-180 € si declara presupuesto y plazo.',
      disclosure: 'Sin afiliación confirmada; priorizar lead de evaluación neutral.',
    },
    sourceUrls: ['https://support.claude.com/en/articles/9266767-what-is-the-team-plan'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'microsoft-365-copilot-business',
    name: 'Microsoft 365 Copilot Business',
    category: 'IA dentro de Microsoft 365',
    short: 'La opción natural si la empresa vive en Outlook, Teams, Word, Excel y PowerPoint y ya paga licencias Business elegibles.',
    bestFor: 'Pymes y departamentos Microsoft-first que quieren adopción rápida dentro del flujo actual de trabajo.',
    caveat: 'Requiere un plan Microsoft 365 Business elegible; la disponibilidad y promociones pueden variar por mercado.',
    useCases: ['workspace', 'general-ai', 'knowledge'],
    stacks: ['microsoft'],
    pricing: {
      display: 'Desde $21/usuario/mes anual; promoción vista de $18 hasta 30/09/2026 en la página US.',
      annualSeatUsd: 21,
      promoSeatUsd: 18,
      notes: 'No incluye el coste base del plan Microsoft 365 Business requerido.',
    },
    scores: { setup: 86, security: 86, automation: 72, brand: 58, governance: 86, lowCost: 68 },
    monetization: {
      path: 'qualified-lead',
      status: 'lead-capture',
      cta: 'Calcular coste total Microsoft 365 + Copilot',
      url: 'https://www.microsoft.com/en-us/microsoft-365-copilot/pricing',
      estimatedValueEur: 'Lead partner/CSP/consultoría: 50-400 € según asientos.',
      disclosure: 'Captura de lead para evaluación; no se muestra como partner oficial.',
    },
    sourceUrls: ['https://www.microsoft.com/en-us/microsoft-365-copilot/pricing'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'google-workspace-gemini',
    name: 'Google Workspace con Gemini',
    category: 'IA dentro de Google Workspace',
    short: 'Compra lógica si la empresa trabaja en Gmail, Docs, Meet y Drive y quiere IA integrada sin comprar otro asistente externo.',
    bestFor: 'Equipos Google-first que priorizan colaboración, correo, reuniones y documentación diaria.',
    caveat: 'Es una suite completa; no conviene compararla solo como add-on de IA sin contar el coste/base workspace.',
    useCases: ['workspace', 'general-ai', 'meetings'],
    stacks: ['google'],
    pricing: {
      display: 'Business Standard listado a $14/usuario/mes anual; Business Plus a $22. Promos temporales para nuevos clientes.',
      annualSeatUsd: 14,
      notes: 'La página oficial muestra Gemini en Gmail, Docs, Meet y más dentro de los planes Business.',
    },
    scores: { setup: 84, security: 82, automation: 65, brand: 55, governance: 82, lowCost: 79 },
    monetization: {
      path: 'qualified-lead',
      status: 'lead-capture',
      cta: 'Comparar Google Workspace vs Microsoft 365',
      url: 'https://workspace.google.com/pricing',
      estimatedValueEur: 'Lead migración/workspace: 40-300 € según asientos.',
      disclosure: 'Captura de lead neutral; promociones y precios deben verificarse antes de compra.',
    },
    sourceUrls: ['https://workspace.google.com/pricing'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'fireflies-business',
    name: 'Fireflies.ai Business',
    category: 'Reuniones y notas IA',
    short: 'Producto vertical claro para transcripción, resúmenes, búsqueda de reuniones y handoff a ventas/operaciones.',
    bestFor: 'Equipos comerciales, customer success, recruiting y managers con muchas reuniones grabables.',
    caveat: 'Las funciones avanzadas usan créditos IA; revisar política de grabación, consentimiento y privacidad por país.',
    useCases: ['meetings', 'automation'],
    stacks: ['microsoft', 'google', 'mixed'],
    pricing: {
      display: 'Business: $19/asiento/mes anual; Enterprise: $39/asiento/mes anual.',
      annualSeatUsd: 19,
      monthlySeatUsd: 29,
      notes: 'Business incluye 30 créditos IA; Enterprise 50 créditos IA según página oficial.',
    },
    scores: { setup: 90, security: 74, automation: 84, brand: 42, governance: 70, lowCost: 83 },
    monetization: {
      path: 'affiliate',
      status: 'affiliate-ready',
      cta: 'Probar Fireflies con enlace de partner',
      url: 'https://fireflies.ai/affiliate',
      estimatedValueEur: 'Afiliación reportada: 10% de pagos referidos; lead B2B adicional 15-120 €.',
      disclosure: 'Puede generar comisión si se aprueba/usa el programa de afiliados.',
    },
    sourceUrls: ['https://fireflies.ai/pricing', 'https://fireflies.getrewardful.com/signup'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'jasper-business',
    name: 'Jasper',
    category: 'Marketing y marca con IA',
    short: 'Más especializado que un chatbot genérico cuando el problema es contenido on-brand, campañas, estilo y gobierno de marca.',
    bestFor: 'Marketing teams, agencias y empresas con varias marcas, guías de estilo y necesidad de producción consistente.',
    caveat: 'El plan Business es personalizado y puede exigir compromiso anual; Pro es más claro para validación inicial.',
    useCases: ['marketing'],
    stacks: ['mixed', 'google', 'microsoft'],
    pricing: {
      display: 'Pro: $59/asiento/mes anual o $69 mensual; Business con precio personalizado.',
      annualSeatUsd: 59,
      monthlySeatUsd: 69,
      custom: true,
      notes: 'Business añade agentes complejos, no-code builder, API, gobierno y soporte dedicado.',
    },
    scores: { setup: 76, security: 74, automation: 76, brand: 94, governance: 78, lowCost: 48 },
    monetization: {
      path: 'affiliate',
      status: 'ready-to-apply',
      cta: 'Comparar Jasper para marketing',
      url: 'https://www.jasper.ai/legal/affiliates',
      estimatedValueEur: 'Afiliación/partner + lead marketing: 40-350 € si hay presupuesto.',
      disclosure: 'Programa sujeto a aprobación y términos del proveedor.',
    },
    sourceUrls: ['https://www.jasper.ai/pricing', 'https://www.jasper.ai/legal/affiliates'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'notion-business-ai',
    name: 'Notion Business con AI',
    category: 'Wiki, proyectos y conocimiento',
    short: 'Buen candidato cuando la empresa quiere ordenar documentación, proyectos y conocimiento antes de comprar IA más compleja.',
    bestFor: 'Startups, agencias, ops y equipos que necesitan wiki, bases de datos, documentación y búsqueda asistida.',
    caveat: 'No sustituye un asistente general potente ni una suite office completa; encaja mejor si Notion será la fuente de verdad.',
    useCases: ['knowledge', 'workspace'],
    stacks: ['mixed', 'google'],
    pricing: {
      display: 'Business: $20/asiento/mes en página oficial consultada.',
      annualSeatUsd: 20,
      notes: 'La facturación depende del número de miembros pagados del workspace.',
    },
    scores: { setup: 82, security: 76, automation: 68, brand: 64, governance: 72, lowCost: 72 },
    monetization: {
      path: 'affiliate',
      status: 'affiliate-ready',
      cta: 'Ver Notion para conocimiento interno',
      url: 'https://www.notion.com/affiliates',
      estimatedValueEur: 'Afiliación Notion + lead implementación: 20-250 €.',
      disclosure: 'Puede generar comisión en altas net-new elegibles tras upgrade.',
    },
    sourceUrls: ['https://www.notion.com/pricing', 'https://www.notion.com/affiliates'],
    lastChecked: '2026-09-12',
  },
  {
    id: 'zapier-ai-automation',
    name: 'Zapier AI Automation',
    category: 'Automatización y agentes conectados',
    short: 'Ideal cuando el dolor no es el chatbot, sino conectar apps, tareas, formularios, CRM y procesos repetitivos con IA.',
    bestFor: 'Operaciones, ventas, soporte y back-office que necesitan automatizar sin construir software a medida.',
    caveat: 'El coste depende de tareas y uso; es fácil infraestimar volumen si no se modelan workflows concretos.',
    useCases: ['automation'],
    stacks: ['mixed', 'google', 'microsoft'],
    pricing: {
      display: 'Modelo por tareas; AI steps, code y SDK siguen pricing task-based según documentación reciente.',
      custom: true,
      notes: 'Team y Enterprise añaden workspace compartido, gobierno, SSO y retención personalizada.',
    },
    scores: { setup: 79, security: 72, automation: 95, brand: 45, governance: 74, lowCost: 55 },
    monetization: {
      path: 'qualified-lead',
      status: 'lead-capture',
      cta: 'Diseñar mapa de automatización rentable',
      url: 'https://zapier.com/pricing',
      estimatedValueEur: 'Lead consultoría/automatización: 75-750 € según proceso.',
      disclosure: 'Captura de lead para evaluación; no se afirma relación oficial.',
    },
    sourceUrls: ['https://zapier.com/pricing', 'https://help.zapier.com/hc/en-us/articles/46597632373389-AI-by-Zapier-new-model-based-pricing-starting-June-15-2026'],
    lastChecked: '2026-09-12',
  },
];
